import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, Mail, User, ShieldCheck, AlertCircle, ChevronRight, Loader2, Building2 } from "lucide-react";
import { useCSuiteAuth } from "../../state/CSuiteAuthContext";
import { csuiteApi } from "../../lib/csuiteApi";
import useSEO from "../../lib/useSEO";

// ─── Shell ────────────────────────────────────────────────────────────────
function Shell({ title, sub, children }) {
  return (
    <div className="csuite-cherry min-h-screen text-[var(--tx1)] grid place-items-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <Building2 size={20} style={{ color: "#7A1F2B" }}/>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#5C1F2A" }}>Executive Portal</div>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight" style={{ color: "#2A0A10" }}>{title}</h1>
        {sub && <p className="text-sm mt-2 leading-relaxed" style={{ color: "#5C1F2A" }}>{sub}</p>}
        <div className="mt-8">{children}</div>
        <div className="mt-10 font-mono text-[10px] uppercase tracking-[0.15em] text-center" style={{ color: "#7A4750" }}>
          Executive & PM access · Operator / CIO / CFO
        </div>
      </div>
    </div>
  );
}

function ErrorBanner({ children }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-sm" data-testid="auth-error">
      <AlertCircle size={14} className="text-red-700 shrink-0 mt-0.5"/>
      <div className="text-xs text-red-900">{children}</div>
    </div>
  );
}

function InfoBanner({ children, tone = "blue" }) {
  if (!children) return null;
  const cls = tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-900"
                               : "bg-sky-50 border-sky-200 text-sky-900";
  return <div className={`text-xs p-3 mb-3 border rounded-sm ${cls}`} data-testid="auth-info">{children}</div>;
}

// ─── Login ────────────────────────────────────────────────────────────────
export function CSuiteLogin() {
  const { login } = useCSuiteAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await login(email.trim().toLowerCase(), password);
      const next = new URLSearchParams(loc.search).get("next");
      // CIO/CFO default to executive dashboard; OPERATOR (or any) go to projects
      const target = next || (r.user?.role === "OPERATOR" ? "/projects" : "/csuite/dashboard");
      nav(target, { replace: true });
    } catch (ex) {
      const msg = ex.message || "";
      setErr(msg);
      if (/not verified/i.test(msg)) {
        nav("/csuite/verify", { state: { email: email.trim().toLowerCase() } });
      }
    } finally { setBusy(false); }
  };

  return (
    <Shell title="Welcome back" sub="Sign in to the executive dashboard with your registered email & password.">
      <InfoBanner>{loc.state?.message}</InfoBanner>
      <form onSubmit={submit} className="space-y-3" data-testid="csuite-login-form">
        <ErrorBanner>{err}</ErrorBanner>
        <Field icon={<Mail size={13}/>} label="Organization email">
          <input type="email" className="fld" autoComplete="email" required value={email}
                 onChange={(e) => setEmail(e.target.value)} placeholder="cio@yourcompany.com"
                 data-testid="csuite-login-email"/>
        </Field>
        <Field icon={<Lock size={13}/>} label="Password">
          <input type="password" className="fld" autoComplete="current-password" required value={password}
                 onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                 data-testid="csuite-login-password"/>
        </Field>
        <button type="submit" disabled={busy} className="btn btn-primary w-full justify-center mt-2" data-testid="csuite-login-submit">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <ChevronRight size={13}/>} Sign in
        </button>
      </form>
      <div className="mt-6 text-xs text-zinc-600 text-center">
        Don't have an executive account?{" "}
        <Link to="/csuite/signup" className="text-[var(--navy)] font-semibold underline-offset-2 hover:underline" data-testid="link-to-signup">Request access</Link>
      </div>
    </Shell>
  );
}

// ─── Signup ───────────────────────────────────────────────────────────────
export function CSuiteSignup() {
  const nav = useNavigate();
  useSEO({
    title: "C-Suite Executive Portal — Signup for CIOs & CFOs | Capittance",
    description: "Create your Capittance Executive Portal account. Private tenant workspace, live SAP S/4HANA + Dynamics 365 cockpits, Board Pack in 60 seconds, and NAVBOT — Claude 4.5 executive AI — for CIOs, CFOs and steering-committee leads.",
    path: "/csuite/signup",
  });
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [role, setRole] = useState("CIO");
  const [terms, setTerms] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // Capture ?ref= (from a referrer's shareable link) once and persist so
  // it survives the /csuite/verify hop before we submit to the backend.
  const [ref, setRef] = useState(() => {
    try {
      const url = new URL(window.location.href);
      const q = url.searchParams.get("ref");
      if (q) { sessionStorage.setItem("harmonizer_ref", q); return q; }
      return sessionStorage.getItem("harmonizer_ref") || "";
    } catch { return ""; }
  });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    if (password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (!company.trim()) { setErr("Company / organization name is required"); return; }
    if (!terms) { setErr("Please accept the Terms & Agreements before continuing"); return; }
    setBusy(true);
    try {
      const r = await csuiteApi.signup({ username: username.trim(), password, email: email.trim().toLowerCase(), role, ref: ref || undefined, company: company.trim() });
      try { sessionStorage.removeItem("harmonizer_ref"); } catch { /* noop */ }
      try {
        const { trackPosterSignup } = await import("../../lib/posterAttribution");
        trackPosterSignup(email.trim().toLowerCase());
      } catch { /* noop */ }
      nav("/csuite/verify", { state: { email: email.trim().toLowerCase(), devCode: r.dev_code } });
    } catch (ex) { setErr(ex.message || "Sign-up failed"); }
    finally { setBusy(false); }
  };

  return (
    <Shell title="Request access" sub="Sign up as Operator (PM), CIO or CFO. We'll email you a 6-digit code to verify your identity.">
      <form onSubmit={submit} className="space-y-3" data-testid="csuite-signup-form">
        <ErrorBanner>{err}</ErrorBanner>
        {ref && (
          <div className="p-3 rounded-sm border border-amber-200 bg-amber-50 text-xs text-amber-900 flex items-start gap-2" data-testid="signup-referral-banner">
            <span className="text-amber-600 mt-0.5">🎁</span>
            <span>Invited via referral code <span className="font-mono font-semibold">{ref}</span> — once your first payment clears, you and your friend each get one free month applied to your next invoice.</span>
          </div>
        )}
        <Field icon={<User size={13}/>} label="Username">
          <input className="fld" required minLength={3} maxLength={50} value={username}
                 onChange={(e) => setUsername(e.target.value)} placeholder="e.g. j.smith"
                 data-testid="csuite-signup-username"/>
        </Field>
        <Field icon={<Mail size={13}/>} label="Organization email">
          <input type="email" className="fld" required value={email}
                 onChange={(e) => setEmail(e.target.value)} placeholder="cio@yourcompany.com"
                 data-testid="csuite-signup-email"/>
        </Field>
        <Field icon={<User size={13}/>} label="Company / organization">
          <input className="fld" required maxLength={120} value={company}
                 onChange={(e) => setCompany(e.target.value)} placeholder="e.g. Acme Corp"
                 data-testid="csuite-signup-company"/>
          <div className="mt-1 text-[10px] text-zinc-500 font-mono">Your team gets its own private workspace — projects, users and dashboards stay isolated from other tenants.</div>
        </Field>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500 mb-1.5">Role</label>
          <div className="grid grid-cols-3 gap-2" data-testid="csuite-signup-role">
            {["OPERATOR", "CIO", "CFO"].map(r => (
              <button type="button" key={r}
                onClick={() => setRole(r)}
                data-testid={`role-${r}`}
                className={`px-3 py-3 rounded-sm border text-xs transition-colors ${role === r ? "border-[var(--navy)] bg-[var(--surface)] text-[var(--navy)] font-semibold" : "border-[var(--border)] hover:bg-zinc-50 text-zinc-700"}`}>
                {r === "CIO" ? "CIO" : r === "CFO" ? "CFO" : "Operator (PM)"}
              </button>
            ))}
          </div>
        </div>
        <Field icon={<Lock size={13}/>} label="Password">
          <input type="password" className="fld" required minLength={8} value={password}
                 onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters"
                 data-testid="csuite-signup-password"/>
        </Field>
        <Field icon={<Lock size={13}/>} label="Confirm password">
          <input type="password" className="fld" required value={confirm}
                 onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password"
                 data-testid="csuite-signup-confirm"/>
        </Field>
        <label className="flex items-start gap-2 text-xs text-zinc-700 leading-relaxed p-3 border border-[var(--border)] rounded-sm bg-zinc-50/50" data-testid="csuite-signup-terms-block">
          <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} className="mt-0.5" data-testid="csuite-signup-terms" />
          <span>I accept the <a href="/terms" target="_blank" rel="noreferrer" className="underline">Terms &amp; Agreements</a>. I understand the Executive Portal is a <strong>paid subscription</strong> ($200 Starter or $350 Platinum), payments are <strong>non-refundable</strong>, I can cancel at any time with <strong>no proration</strong>, and access ends immediately at cancellation.</span>
        </label>
        <button type="submit" disabled={busy} className="btn btn-primary w-full justify-center mt-2" data-testid="csuite-signup-submit">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <ShieldCheck size={13}/>} Send verification code
        </button>
      </form>
      <div className="mt-6 text-xs text-zinc-600 text-center">
        Already verified?{" "}
        <Link to="/csuite" className="text-[var(--navy)] font-semibold underline-offset-2 hover:underline" data-testid="link-to-login">Sign in</Link>
      </div>
    </Shell>
  );
}

// ─── Verify Code ─────────────────────────────────────────────────────────
export function CSuiteVerify() {
  const { verify } = useCSuiteAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const initialEmail = loc.state?.email || "";
  const initialDevCode = loc.state?.devCode || "";

  const [email] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { setErr("Missing email — please sign up again"); return; }
    setErr(""); setInfo(""); setBusy(true);
    try {
      const r = await verify(email, code.trim());
      const role = r.user?.role;
      const hasSub = r.user?.subscription?.status === "active";
      // Force payment before landing anywhere useful, unless they're already
      // an active subscriber (rare) or an operator on a shared/free workspace.
      let target;
      if (role === "OPERATOR") {
        target = hasSub ? "/projects" : "/signup?next=/projects";
      } else if (role === "ADMIN") {
        target = "/csuite/dashboard";
      } else {
        target = hasSub ? "/csuite/dashboard" : "/signup?next=/csuite/dashboard";
      }
      nav(target, { replace: true });
    } catch (ex) { setErr(ex.message || "Verification failed"); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setErr(""); setInfo("");
    try {
      const r = await csuiteApi.requestCode(email);
      setInfo(r.dev_code ? `Sent. (Dev code: ${r.dev_code})` : "A fresh code has been sent to your email.");
    } catch (ex) { setErr(ex.message || "Failed to resend code"); }
  };

  if (!email) {
    return (
      <Shell title="Missing email" sub="Please start sign-up first.">
        <Link to="/csuite/signup" className="btn btn-primary">Go to sign-up</Link>
      </Shell>
    );
  }

  return (
    <Shell title="Verify your email" sub={<>We sent a 6-digit code to <span className="font-mono font-semibold text-zinc-800">{email}</span>. Enter it below to activate your account.</>}>
      <form onSubmit={submit} className="space-y-3" data-testid="csuite-verify-form">
        {initialDevCode && (
          <InfoBanner tone="amber">
            <span className="font-mono">Dev mode — your code: <strong>{initialDevCode}</strong></span>
          </InfoBanner>
        )}
        <InfoBanner>{info}</InfoBanner>
        <ErrorBanner>{err}</ErrorBanner>
        <Field icon={<ShieldCheck size={13}/>} label="6-digit code">
          <input className="fld font-mono tracking-[0.4em] text-center text-lg" required
                 inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                 value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                 placeholder="••••••" data-testid="csuite-verify-code"/>
        </Field>
        <button type="submit" disabled={busy || code.length !== 6} className="btn btn-primary w-full justify-center mt-2" data-testid="csuite-verify-submit">
          {busy ? <Loader2 size={13} className="animate-spin"/> : <ChevronRight size={13}/>} Verify & continue
        </button>
      </form>
      <div className="mt-5 text-xs text-zinc-600 text-center">
        Didn't get it?{" "}
        <button type="button" onClick={resend} className="text-[var(--navy)] font-semibold underline-offset-2 hover:underline" data-testid="csuite-resend">Resend code</button>
      </div>
    </Shell>
  );
}

// ─── Field primitive ─────────────────────────────────────────────────────
function Field({ icon, label, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500 mb-1.5">
        {icon}{label}
      </label>
      {children}
    </div>
  );
}
